import { U21_NO_VALUE, U21Chip, U21Spacer } from 'app/shared/u21-ui/components';
import { CONDITIONAL_OPERATORS_MAP } from 'app/modules/detectionModels/constants';
import { DmOperator } from 'app/modules/rulesAdvanced/types/RulesRepresentation';

interface ConditionValueProps {
  value: any; // need to come back and clean up once I fix rule condition typing ...
  swapWithSnapshotValue?: boolean;
}

type AvailableMathOperators =
  | 'ADD'
  | 'SUB'
  | 'MUL'
  | 'DIV'
  | 'ABS'
  | 'LOG'
  | 'EXP'
  | 'SQRT'; // see DmOperator

const AvailableMathOperatorsMap: Record<AvailableMathOperators, string> = {
  ADD: '+',
  SUB: '-',
  MUL: '*',
  DIV: '/',
  ABS: 'abs',
  LOG: 'log',
  EXP: 'exp',
  SQRT: 'sqrt',
};

export const getFriendlyOperator = (operator: DmOperator) => {
  try {
    return operator in AvailableMathOperatorsMap
      ? AvailableMathOperatorsMap[operator]
      : CONDITIONAL_OPERATORS_MAP[operator].text;
  } catch {
    return operator; // default to operator on lookup failure
  }
};

const renderSnapshotValue = (snapshotValue: unknown) => {
  if (typeof snapshotValue === 'string' || typeof snapshotValue === 'number') {
    return snapshotValue;
  } else if (typeof snapshotValue === 'boolean') {
    return snapshotValue ? 'true' : 'false';
  } else if (snapshotValue === null) {
    return 'null';
  }
  return U21_NO_VALUE; // run by Maranda first
};

export const ConditionValue = ({
  value,
  swapWithSnapshotValue = false,
}: ConditionValueProps) => {
  if (value === null) {
    return null;
  }

  if (Object.keys(AvailableMathOperatorsMap).includes(value.operator)) {
    const el = value.operands.map((ope, index) => (
      <ConditionValue
        // eslint-disable-next-line react/no-array-index-key
        key={index}
        value={ope}
        swapWithSnapshotValue={swapWithSnapshotValue}
      />
    ));

    return (
      <U21Spacer horizontal>
        {el[0]} <span>{getFriendlyOperator(value.operator)}</span> {el.slice(1)}
      </U21Spacer>
    );
  }

  if (value.type === 'VARIABLE') {
    return (
      <U21Chip>
        {swapWithSnapshotValue
          ? renderSnapshotValue(value.snapshot_value)
          : value.name}
      </U21Chip>
    );
  }

  if (value.type === 'DURATION') {
    return (
      <span>
        {value.value} {value.unit.toLowerCase()}(s) ago
      </span>
    );
  }

  if (Array.isArray(value.value) && swapWithSnapshotValue) {
    return value.value.map((item) => <U21Chip key={item}>{item}</U21Chip>);
  } else if (Array.isArray(value.value)) {
    return <U21Chip>{value.value.length} values</U21Chip>;
  }

  return <span>{value.value}</span>;
};
